import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useConfig } from "../lib/ConfigContext";
import {
  Check, X as XIcon, Quote, Sparkles, Crown, Zap, Phone, MessageCircle,
  ArrowRight, Home, Building2, Hammer, ShieldCheck,
} from "lucide-react";
import usePageTitle from "../lib/usePageTitle";

/**
 * v2.65 — Public Plans Preview page.
 *
 * Route: /plans-preview (no auth, optimized for WhatsApp share)
 * Used internally by Admin/Sales/Caller team via "Share Plans" button.
 *
 * Mirrors the rich /pricing card layout (tagline → price → purpose → benefits →
 * limitations → pitch quote) but for ALL THREE audiences (client / dealer /
 * builder-owner) and without any "buy" gating. CTA always "Get Started" → /access.
 *
 * All copy is admin-editable from /admin?tab=plans (admin_plans collection).
 */

const ROLE_META = {
  client: {
    icon: Home,
    eyebrow: "For Buyers / Renters",
    title: "Client Plans",
    sub: "Discover, save & enquire on verified properties",
    iconClass: "bg-[#EEF4FF] text-[#0044CC]",
  },
  dealer: {
    icon: Building2,
    eyebrow: "For Property Dealers",
    title: "Dealer Plans",
    sub: "Manage listings, leads & clients professionally",
    iconClass: "bg-gradient-to-br from-[#0055FF] to-[#2D7BFF] text-white",
  },
  builder: {
    icon: Hammer,
    eyebrow: "For Builders / Owners",
    title: "Builder / Owner Plans",
    sub: "Launch projects, manage inventory & grow presence",
    iconClass: "bg-[#F5F3FF] text-[#6D28D9]",
  },
};

const TIER_VISUAL = {
  normal: {
    icon: <Zap size={14} />,
    accent: "border-[#E4E4E7] bg-white",
    badge: "border-[#52525B] text-[#52525B]",
  },
  basic: {
    icon: <Zap size={14} />,
    accent: "border-[#E4E4E7] bg-white",
    badge: "border-[#52525B] text-[#52525B]",
  },
  prime: {
    icon: <Sparkles size={14} />,
    accent: "border-[#FDE68A] bg-[#FFFBEB]",
    badge: "border-[#92400E] text-[#92400E]",
    featured: true,
  },
  pro: {
    icon: <Sparkles size={14} />,
    accent: "border-[#FDE68A] bg-[#FFFBEB]",
    badge: "border-[#92400E] text-[#92400E]",
    featured: true,
  },
  premium: {
    icon: <Crown size={14} />,
    accent: "border-[#0055FF] bg-[#EEF4FF] ring-2 ring-[#0055FF]/20",
    badge: "border-[#0055FF] text-[#0055FF]",
    featured: true,
  },
};

const VALIDITY_LABEL = (p) => {
  const months = p.duration_months || (p.duration_days ? Math.round(p.duration_days / 30) : null);
  if (!months) return "—";
  return months >= 12 ? "1 year" : months > 1 ? `${months} months` : `${months} month`;
};

const tierKey = (p) => {
  const id = (p.legacy_id || p.id || "").toLowerCase();
  for (const k of Object.keys(TIER_VISUAL)) if (id.includes(k)) return k;
  return "normal";
};

export default function PlansPreview() {
  usePageTitle("HouseEntry Plans — Compare & Choose");
  const { content, settings } = useConfig();
  const [plans, setPlans] = useState({ client: [], dealer: [], builder: [] });
  const [loading, setLoading] = useState(true);

  const heroIntro = "Each plan is a complete property-buying decision system — not just listing access.";
  const supportPhone = content?.footer?.phone || settings?.contact_phone || "";
  const supportWa = (content?.footer?.social || {}).whatsapp || supportPhone;
  // v2.66 — All hero + CTA copy is admin-editable via /admin?tab=content → "Plans Preview"
  const pp = content?.plans_preview || {};
  const heroEyebrow = pp.hero_eyebrow || "Subscription Plans";
  const heroA = pp.hero_heading_a || "Pick a plan that";
  const heroB = pp.hero_heading_b || "closes more deals";
  const heroSub = pp.hero_sub || heroIntro;
  const ctaEyebrow = pp.cta_eyebrow || "Ready to Get Started?";
  const ctaHeading = pp.cta_heading || "Apna best plan chuniye, ya humse baat karein";
  const ctaSub = pp.cta_sub || "Account banane se pehle bhi humare expert team aapko free consultation degi. Koi obligation nahi.";
  const ctaCreateLabel = pp.cta_create_label || "Create Account";
  const ctaWaLabel = pp.cta_whatsapp_label || "Talk to Our Team";
  const ctaWaMessage = pp.cta_whatsapp_message || "Hi HouseEntry team, I want to know more about plans.";
  const footnote = pp.footnote || "Secure payments via Razorpay · Validity per plan";

  useEffect(() => {
    Promise.all([
      api.get("/plans?role=client").then((r) => r.data).catch(() => []),
      api.get("/plans?role=dealer").then((r) => r.data).catch(() => []),
    ]).then(([clientPlans, dealerPlans]) => {
      const builderPlans = dealerPlans.filter((p) => (p.audiences || []).includes("builder"));
      const dealerOnly = dealerPlans.filter((p) => !p.audiences || p.audiences.includes("dealer"));
      setPlans({
        client: clientPlans,
        dealer: dealerOnly.length ? dealerOnly : dealerPlans,
        builder: builderPlans.length ? builderPlans : dealerPlans, // fallback share if no separate builder plans
      });
      setLoading(false);
    });
  }, []);

  const sections = useMemo(() => {
    return ["client", "dealer", "builder"]
      .map((k) => ({ key: k, items: plans[k] || [], ...ROLE_META[k] }))
      .filter((s) => s.items.length > 0);
  }, [plans]);

  const waText = encodeURIComponent(
    "Hi, here are the HouseEntry plans and features.\nExplore and choose the option that fits your needs best.\n\n" +
      (typeof window !== "undefined" ? window.location.origin : "https://houseentry.in") + "/plans-preview"
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-50" data-testid="plans-preview-page">
      {/* Sticky compact brand header */}
      <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="max-w-[1180px] mx-auto px-4 sm:px-8 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-[18px] tracking-tight text-zinc-900" data-testid="plans-preview-brand">
            House<span className="text-[#E53935]">Entry</span>
          </Link>
          <Link
            to="/access"
            className="inline-flex items-center gap-1.5 btn-gradient rounded-xl px-3.5 py-1.5 text-[12px] font-semibold transition-all hover:shadow-lg hover:shadow-blue-500/25 active:scale-95"
            data-testid="plans-preview-access-cta"
          >
            Get Started <ArrowRight size={12} />
          </Link>
        </div>
      </header>

      <div className="max-w-[1180px] mx-auto px-4 sm:px-8 lg:px-10 pt-6 pb-14 sm:pt-10">
        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full bg-[#EEF4FF] border border-[#C7D9FF] text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#0044CC]" data-testid="plans-preview-eyebrow">
            <Sparkles size={11} /> {heroEyebrow}
          </div>
          <h1 className="font-heading font-bold text-[26px] sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-zinc-900" data-testid="plans-preview-heading">
            {heroA} <span className="text-[#0055FF]">{heroB}</span>
          </h1>
          <p className="mt-3 sm:mt-4 text-[14px] sm:text-base text-zinc-600 leading-relaxed" data-testid="plans-preview-sub">
            {heroSub}
          </p>
        </div>

        {loading && <div className="text-center text-[#A1A1AA] py-10">Loading plans…</div>}

        {sections.map((sec) => (
          <PlanSection key={sec.key} section={sec} />
        ))}

        {!loading && sections.length === 0 && (
          <div className="text-center py-12 text-[#52525B]">
            Plans abhi configure nahi hue. Team se sampark karein.
          </div>
        )}

        {/* CTA Block */}
        <div className="mt-14 mx-auto max-w-3xl rounded-2xl bg-gradient-to-br from-[#09090B] via-[#18181B] to-[#27272A] text-white p-8 sm:p-10 text-center" data-testid="plans-preview-cta-block">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DD3FC] mb-3" data-testid="plans-cta-eyebrow">{ctaEyebrow}</div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mb-3" data-testid="plans-cta-heading">
            {ctaHeading}
          </h2>
          <p className="text-[13.5px] text-[#A1A1AA] mb-6 max-w-xl mx-auto" data-testid="plans-cta-sub">
            {ctaSub}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/access"
              className="inline-flex items-center gap-2 bg-white text-[#09090B] hover:bg-[#0055FF] hover:text-white px-5 py-3 rounded-xl text-[13.5px] font-bold transition-all shadow-lg active:scale-95"
              data-testid="plans-preview-create-account"
            >
              {ctaCreateLabel} <ArrowRight size={14} />
            </Link>
            {supportWa && (
              <a
                href={`https://wa.me/${supportWa.replace(/\D/g, "")}?text=${encodeURIComponent(ctaWaMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FAB54] text-white px-5 py-3 rounded-xl text-[13.5px] font-bold transition-all shadow-lg active:scale-95"
                data-testid="plans-preview-whatsapp"
              >
                <MessageCircle size={14} /> {ctaWaLabel}
              </a>
            )}
            {supportPhone && (
              <a
                href={`tel:${supportPhone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-5 py-3 rounded-xl text-[13.5px] font-semibold transition-all active:scale-95"
                data-testid="plans-preview-call"
              >
                <Phone size={14} /> {supportPhone}
              </a>
            )}
          </div>
        </div>

        <footer className="text-center text-[11px] text-[#A1A1AA] mt-10 flex items-center justify-center gap-2" data-testid="plans-preview-footnote">
          <ShieldCheck size={12} className="text-[#16A34A]" />
          {footnote}
        </footer>
        <div className="text-center text-[10.5px] text-[#A1A1AA] mt-3">
          © {new Date().getFullYear()} HouseEntry Solutions. All rights reserved.
        </div>
      </div>
    </div>
  );
}

function PlanSection({ section }) {
  const Icon = section.icon;
  // De-dupe — if dealer & builder fall back to same plans, hide the duplicate row
  return (
    <section className="mb-10 sm:mb-14" data-testid={`plans-section-${section.key}`}>
      <div className="flex items-start gap-3 mb-5">
        <div className={`w-11 h-11 rounded-xl ${section.iconClass} flex items-center justify-center shadow-sm shrink-0`}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#0044CC] mb-1">{section.eyebrow}</div>
          <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight text-zinc-900">{section.title}</h2>
          <div className="text-[12.5px] text-zinc-600">{section.sub}</div>
        </div>
      </div>
      <div className={`grid grid-cols-1 ${section.items.length === 1 ? "sm:grid-cols-1" : section.items.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"} gap-5 lg:gap-6 items-stretch`}>
        {section.items.map((p) => <RichPlanCard key={p.id || p.code || p.name} plan={p} />)}
      </div>
    </section>
  );
}

function RichPlanCard({ plan }) {
  const tier = tierKey(plan);
  const visual = TIER_VISUAL[tier];
  const price = plan.price_inr ?? plan.price ?? 0;

  return (
    <div
      className={`rounded-sm p-5 sm:p-6 lg:p-7 flex flex-col relative border w-full min-w-0 h-full ${visual.accent} transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/10`}
      data-testid={`plan-card-${plan.id || tier}`}
    >
      {visual.featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#0055FF] to-[#7C3AED] text-white text-[9.5px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full shadow-md">
          Recommended
        </span>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-1 border rounded-sm ${visual.badge}`}>
          {visual.icon} {plan.name || tier}
        </div>
      </div>

      {plan.tagline && (
        <div className="text-[12.5px] text-zinc-600 italic mb-3" data-testid={`plan-tagline-${plan.id}`}>
          {plan.tagline}
        </div>
      )}

      {/* Price */}
      <div className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-zinc-900 font-mono-data break-words">
        {price > 0 ? `₹${Number(price).toLocaleString("en-IN")}` : "Free"}
      </div>
      <div className="text-[12px] text-zinc-500 mb-3">/ <span data-testid={`plan-validity-${plan.id}`}>{VALIDITY_LABEL(plan)}</span></div>

      {/* Purpose box */}
      {plan.purpose && (
        <div className="text-[12.5px] text-zinc-900 bg-white border border-[#E4E4E7] rounded-sm p-2.5 mb-4" data-testid={`plan-purpose-${plan.id}`}>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A1A1AA] mb-0.5">Purpose</div>
          {plan.purpose}
        </div>
      )}

      {/* v2.71.12 — Schema-driven sections (preferred, matches /pricing).
          Falls back to legacy benefits/limitations arrays only if sections is empty. */}
      {(plan.sections && plan.sections.length > 0) ? (
        <div className="flex-1 space-y-4" data-testid={`plan-sections-${plan.id}`}>
          {plan.sections.map((s, sIdx) => {
            const isLimit = s.type === "limitations";
            return (
              <div key={s.id} className={sIdx > 0 ? "pt-3 border-t border-[#E4E4E7]" : ""}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${isLimit ? "text-[#A1A1AA]" : "text-zinc-600"}`}>
                  {s.title}
                </div>
                <ul className="space-y-1.5" data-testid={`plan-section-${s.id}`}>
                  {(s.items || []).map((it) => (
                    <li key={it.id} className={`flex items-start gap-2 ${isLimit ? "text-[12px] text-zinc-500" : "text-[13px] text-zinc-900"}`}>
                      {it.icon ? (
                        <span className="text-[14px] flex-shrink-0 leading-tight">{it.icon}</span>
                      ) : isLimit ? (
                        <XIcon size={12} className="text-[#A1A1AA] mt-0.5 flex-shrink-0" />
                      ) : (
                        <Check size={14} className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                      )}
                      <span>{it.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {/* Benefits */}
          {plan.benefits && plan.benefits.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1.5">What you get</div>
              <ul className="space-y-1.5 flex-1" data-testid={`plan-benefits-${plan.id}`}>
                {plan.benefits.map((b, i) => (
                  <li key={`${plan.id}-b-${i}`} className="text-[13px] text-zinc-900 flex items-start gap-2">
                    <Check size={14} className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Limitations */}
          {plan.limitations && plan.limitations.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#E4E4E7]" data-testid={`plan-limits-${plan.id}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A1A1AA] mb-1.5">Limitations</div>
              <ul className="space-y-1">
                {plan.limitations.map((l, i) => (
                  <li key={`${plan.id}-l-${i}`} className="text-[12px] text-zinc-500 flex items-start gap-2">
                    <XIcon size={12} className="text-[#A1A1AA] mt-0.5 flex-shrink-0" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Pitch quote */}
      {plan.pitch && (
        <div className="mt-4 p-3 bg-zinc-50 border-l-2 border-[#0055FF] text-[12px] text-zinc-600 italic flex gap-2" data-testid={`plan-pitch-${plan.id}`}>
          <Quote size={12} className="text-[#0055FF] mt-0.5 flex-shrink-0" />
          <span>{plan.pitch}</span>
        </div>
      )}

      {/* CTA */}
      <Link
        to="/access"
        className="mt-5 w-full text-center inline-flex items-center justify-center gap-1.5 btn-gradient rounded-xl py-2.5 text-[13px] font-bold transition-all hover:shadow-lg active:scale-[0.99]"
        data-testid={`plan-card-${plan.id || tier}-cta`}
      >
        Get Started <ArrowRight size={13} />
      </Link>
    </div>
  );
}
